import React, { useMemo, useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";
import mapValues from "lodash/mapValues";
import { Formik, Form, Field } from "formik";
import { Modal, Alert, Input } from "components/bootstrap";
import { Select, Spinner, FormikInput, Link } from "components/common";
import StreamLink from "components/streams/StreamLink";
import IndexSetsTable from "views/logic/fieldactions/ChangeFieldType/IndexSetsTable";
import usePutFieldTypeMutation from "views/logic/fieldactions/ChangeFieldType/hooks/useFieldTypeMutation";
import useStream from "components/streams/hooks/useStream";
import { DocumentationLink } from "components/support";
import DocsHelper from "util/DocsHelper";
import { defaultCompare } from "logic/DefaultCompare";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useSendTelemetryOnMount from "logic/telemetry/useSendTelemetryOnMount";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import FieldSelect from "views/logic/fieldactions/ChangeFieldType/FieldSelect";
import useFieldTypesForMappings from "views/logic/fieldactions/ChangeFieldType/hooks/useFieldTypesForMappings";
import Routes from "routing/Routes";
import usePermissions from "hooks/usePermissions";
import ModalSubmit from "components/common/ModalSubmit";
const StyledSelect = styled(Select)`
  width: 400px;
  margin-bottom: 20px;
`;
const StyledLabel = styled.h5`
  font-weight: bold;
  margin-bottom: 5px;
`;
const failureStreamId = "000000000000000000000004";
const FailureProcessingDocsLink = ({ children = void 0 }) => /* @__PURE__ */ React.createElement(DocumentationLink, { page: DocsHelper.PAGES.INDEXER_FAILURES, displayIcon: true, text: children });
const FailureStreamLink = () => {
  const { t } = useTranslation("search");
  const {
    data: failureStream,
    isFetching: isFetchingFailureStream,
    isError: isErrorFailureStream
  } = useStream(failureStreamId);
  if (isFetchingFailureStream) return /* @__PURE__ */ React.createElement(Spinner, null);
  return /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
    StreamLink,
    {
      stream: isErrorFailureStream ? { id: failureStreamId, title: "Processing and Indexing Failures" } : failureStream
    }
  ), /* @__PURE__ */ React.createElement("i", null, " ", /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "change_field_type.enterprise_plugin_required",
      components: { enterpriseLink: /* @__PURE__ */ React.createElement(Link, { to: Routes.SYSTEM.ENTERPRISE }) }
    }
  )));
};
const rotationHintKey = (type, fieldName) => {
  if (type) {
    return fieldName ? "change_field_type.rotation_hint_type_field" : "change_field_type.rotation_hint_type";
  }
  return fieldName ? "change_field_type.rotation_hint_field" : "change_field_type.rotation_hint";
};
const ChangeFieldTypeModal = ({
  onSubmitCallback = void 0,
  initialSelectedIndexSets,
  onClose,
  showSelectionTable = true,
  showFieldSelect = false,
  initialData = { fieldName: void 0, type: void 0 }
}) => {
  const { t } = useTranslation("search");
  const {
    data: { fieldTypes },
    isLoading: isLoadingFieldTypes
  } = useFieldTypesForMappings();
  const sendTelemetry = useSendTelemetry();
  const { isPermitted } = usePermissions();
  const hasFailureStreamAccess = isPermitted(`streams:read:${failureStreamId}`);
  const fieldTypeOptions = useMemo(
    () => Object.entries(fieldTypes).sort(([, label1], [, label2]) => defaultCompare(label1, label2)).map(([value, label]) => ({
      value,
      label
    })),
    [fieldTypes]
  );
  const [indexSetSelection, setIndexSetSelection] = useState(initialSelectedIndexSets);
  const { putFieldTypeMutation, isLoading: fieldTypeMutationIsLoading } = usePutFieldTypeMutation();
  const onSubmit = useCallback(
    ({ rotate: rotated, field_type: type, field: fieldName }) => {
      putFieldTypeMutation({
        indexSetSelection,
        newFieldType: type,
        rotated,
        field: fieldName
      }).then((responseJson) => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FIELD_VALUE_ACTION.CHANGE_FIELD_TYPE_CHANGED, {
          app_action_value: {
            value: "change-field-type",
            rotated,
            isAllIndexesSelected: indexSetSelection.length === initialSelectedIndexSets.length
          }
        });
        if (onSubmitCallback) {
          const response = mapValues(responseJson, (fieldType) => ({
            id: fieldType.field_name,
            origin: fieldType.origin,
            fieldName: fieldType.field_name,
            type: fieldType.type,
            isReserved: fieldType.is_reserved
          }));
          onSubmitCallback(response);
        }
      }).then(() => onClose());
    },
    [
      indexSetSelection,
      initialSelectedIndexSets.length,
      onClose,
      onSubmitCallback,
      putFieldTypeMutation,
      sendTelemetry
    ]
  );
  useSendTelemetryOnMount(sendTelemetry, TELEMETRY_EVENT_TYPE.SEARCH_FIELD_VALUE_ACTION.CHANGE_FIELD_TYPE_OPENED, {
    app_action_value: "change-field-type-opened"
  });
  const onCancel = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FIELD_VALUE_ACTION.CHANGE_FIELD_TYPE_CLOSED, {
      app_action_value: "change-field-type-closed"
    });
    onClose();
  }, [onClose, sendTelemetry]);
  const initialValues = useMemo(
    () => ({ field: initialData == null ? void 0 : initialData.fieldName, field_type: initialData == null ? void 0 : initialData.type, rotate: true }),
    [initialData == null ? void 0 : initialData.fieldName, initialData == null ? void 0 : initialData.type]
  );
  return /* @__PURE__ */ React.createElement(Formik, { onSubmit, initialValues }, ({ values: { field: fieldName, field_type: type } }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, /* @__PURE__ */ React.createElement("span", null, t("change_field_type.modal_title", { fieldName })))), /* @__PURE__ */ React.createElement(Modal.Body, null, showFieldSelect && /* @__PURE__ */ React.createElement(Field, { name: "field" }, ({ field: { value, name } }) => /* @__PURE__ */ React.createElement(FieldSelect, { indexSetId: initialSelectedIndexSets[0], name, field: value })), /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "change_field_type.impact_warning",
      values: { fieldName },
      components: { bold: /* @__PURE__ */ React.createElement("b", null) }
    }
  ), hasFailureStreamAccess && /* @__PURE__ */ React.createElement(React.Fragment, null, " ", /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "change_field_type.failure_processing_hint",
      components: { docsLink: /* @__PURE__ */ React.createElement(FailureProcessingDocsLink, null), failureStream: /* @__PURE__ */ React.createElement(FailureStreamLink, null) }
    }
  ))), /* @__PURE__ */ React.createElement(StyledLabel, null, fieldName ? t("change_field_type.select_field_type_for", { fieldName }) : t("change_field_type.select_field_type_for_field")), /* @__PURE__ */ React.createElement(Field, { name: "field_type" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(Input, { id: name }, /* @__PURE__ */ React.createElement(
    StyledSelect,
    {
      inputId: name,
      options: fieldTypeOptions,
      name,
      onChange: (newValue) => onChange({ target: { name, value: newValue } }),
      value,
      placeholder: t("change_field_type.select_field_type"),
      disabled: isLoadingFieldTypes,
      "aria-label": t("change_field_type.select_field_type_for_field"),
      required: true
    }
  ))), showSelectionTable && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledLabel, null, t("change_field_type.select_index_sets")), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: type ? "change_field_type.index_sets_hint_with_type" : "change_field_type.index_sets_hint",
      values: { type, fieldName },
      components: { bold: /* @__PURE__ */ React.createElement("b", null) }
    }
  )), /* @__PURE__ */ React.createElement(
    IndexSetsTable,
    {
      field: fieldName,
      setIndexSetSelection,
      fieldTypes,
      initialSelection: initialSelectedIndexSets
    }
  )), /* @__PURE__ */ React.createElement(StyledLabel, null, t("change_field_type.select_rotation_strategy")), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: rotationHintKey(type, fieldName),
      values: { type, fieldName },
      components: { bold: /* @__PURE__ */ React.createElement("b", null) }
    }
  )), /* @__PURE__ */ React.createElement(Field, { name: "rotate" }, ({ field: { name, onChange } }) => /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      label: t("change_field_type.rotate_indices"),
      id: name,
      name,
      onChange
    }
  ))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      disabledSubmit: fieldTypeMutationIsLoading || !(indexSetSelection == null ? void 0 : indexSetSelection.length),
      onCancel,
      submitButtonText: fieldTypeMutationIsLoading ? t("change_field_type.changing") : t("change_field_type.change")
    }
  ))));
};
const LoadingBarrier = ({
  isLoading = false,
  show,
  onSubmitCallback = void 0,
  initialSelectedIndexSets,
  onClose,
  showSelectionTable = true,
  showFieldSelect = false,
  initialData = { fieldName: void 0, type: void 0 }
}) => /* @__PURE__ */ React.createElement(Modal, { show, bsSize: "large", onHide: onClose }, isLoading ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(
  ChangeFieldTypeModal,
  {
    onClose,
    initialSelectedIndexSets,
    onSubmitCallback,
    showSelectionTable,
    showFieldSelect,
    initialData
  }
));
export default LoadingBarrier;
